import React, { createContext, useState, useEffect } from "react";
import { safeSessionStorageSet, hasProperty } from "../utils/helpers";

export const AuthContext = createContext();

const AuthContextProvider = (props) => {
  const [authToken, setAuthToken] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [accessLevel, setAccessLevel] = useState(-1);
  const [packages, setPackages] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // BE URL Testing
  const [baseBE_URL, setBaseBE_URL] = useState(
    window.location.hostname === "app.speqqle.com"
      ? "wss://beprod1.speqqle.com/ws"
      : window.location.hostname === "lab.speqqle.com"
      ? "wss://belab.speqqle.com/ws"
      : process.env.REACT_APP_USE_LOCALHOST_BE === "true" || process.env.REACT_APP_USE_LOCALHOST_BE === "True"
      ? "ws://localhost:8000/ws"
      : "wss://belab.speqqle.com/ws"
  );
  // const [baseBE_URL, setBaseBE_URL] = useState("wss://beprod1.speqqle.com/ws");

  const resetAuth = () => {
    setAuthToken("");
    setIsAdmin(false);
    setIsFirstLogin(true);
    setIsActive(false);
    setAccessLevel(-1);
    setPackages([]);
    setCurrentUser(null);
    // setIsAuthReady(false);
  };

  useEffect(() => {
    const authTokenSession = JSON.parse(
      window.sessionStorage.getItem("authToken")
    );
    if (authTokenSession != null) {
      setAuthToken(authTokenSession);
    }

    const isAdminSession = JSON.parse(window.sessionStorage.getItem("isAdmin"));
    if (isAdminSession != null) {
      setIsAdmin(isAdminSession);
    }

    const isActiveSession = JSON.parse(
      window.sessionStorage.getItem("isActive")
    );
    if (isActiveSession != null) {
      setIsActive(isActiveSession);
    }
    const accessLevelSession = JSON.parse(
      window.sessionStorage.getItem("accessLevel")
    );
    if (accessLevelSession != null) {
      setAccessLevel(accessLevelSession);
    }

    const packagesSession = JSON.parse(
      window.sessionStorage.getItem("packages")
    );
    if (packagesSession != null) {
      setPackages(packagesSession);
    }

    const currentUserSession = JSON.parse(
      window.sessionStorage.getItem("currentUser")
    );
    if (currentUserSession != null) {
      setCurrentUser(currentUserSession);
    }

    const isFirstLoginSession = JSON.parse(
      window.sessionStorage.getItem("isFirstLogin")
    );
    if (isFirstLoginSession != null) {
      setIsFirstLogin(isFirstLoginSession);
    }

    const baseBE_URLSession = JSON.parse(
      window.sessionStorage.getItem("baseBE_URL")
    );
    if (baseBE_URLSession != null) {
      setBaseBE_URL(baseBE_URLSession);
    }

    setIsAuthReady(true);
  }, []);

  // Function to sync UI state with what's actually stored in sessionStorage
  const syncWithSessionStorage = () => {
    try {
      // Helper function to safely parse JSON from sessionStorage
      const safeJsonParse = (key) => {
        try {
          const item = window.sessionStorage.getItem(key);
          if (
            item === null ||
            item === undefined ||
            item === "undefined" ||
            item === ""
          )
            return null;
          return JSON.parse(item);
        } catch (error) {
          console.warn(`Error parsing ${key} from sessionStorage:`, error);
          return null;
        }
      };

      // Read all values from sessionStorage and update state
      const authTokenSession = safeJsonParse("authToken");
      const isAdminSession = safeJsonParse("isAdmin");
      const isActiveSession = safeJsonParse("isActive");
      const accessLevelSession = safeJsonParse("accessLevel");
      const packagesSession = safeJsonParse("packages");
      const currentUserSession = safeJsonParse("currentUser");
      const isFirstLoginSession = safeJsonParse("isFirstLogin");
      const baseBE_URLSession = safeJsonParse("baseBE_URL");

      // Update state with what's actually stored
      if (authTokenSession !== null) setAuthToken(authTokenSession);
      if (isAdminSession !== null) setIsAdmin(isAdminSession);
      if (isActiveSession !== null) setIsActive(isActiveSession);
      if (accessLevelSession !== null) setAccessLevel(accessLevelSession);
      if (packagesSession !== null) setPackages(packagesSession);
      if (currentUserSession !== null) setCurrentUser(currentUserSession);
      if (isFirstLoginSession !== null) setIsFirstLogin(isFirstLoginSession);
      if (baseBE_URLSession !== null) setBaseBE_URL(baseBE_URLSession);
    } catch (error) {
      console.error("Error syncing auth state with sessionStorage:", error);
    }
  };

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "authToken",
      JSON.stringify(authToken)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save authToken to sessionStorage - quota exceeded"
      );
    }
  }, [authToken]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "isAdmin",
      JSON.stringify(isAdmin)
    );
    if (!saveResult) {
      console.warn("Failed to save isAdmin to sessionStorage - quota exceeded");
    }
  }, [isAdmin]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "isActive",
      JSON.stringify(isActive)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save isActive to sessionStorage - quota exceeded"
      );
    }
  }, [isActive]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "accessLevel",
      JSON.stringify(accessLevel)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save accessLevel to sessionStorage - quota exceeded"
      );
    }
  }, [accessLevel]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "packages",
      JSON.stringify(packages)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save packages to sessionStorage - quota exceeded"
      );
    }
  }, [packages]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "currentUser",
      JSON.stringify(currentUser)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save currentUser to sessionStorage - quota exceeded"
      );
    }
  }, [currentUser]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "isFirstLogin",
      JSON.stringify(isFirstLogin)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save isFirstLogin to sessionStorage - quota exceeded"
      );
    }
  }, [isFirstLogin]);

  useEffect(() => {
    const saveResult = safeSessionStorageSet(
      "baseBE_URL",
      JSON.stringify(baseBE_URL)
    );
    if (!saveResult) {
      console.warn(
        "Failed to save baseBE_URL to sessionStorage - quota exceeded"
      );
    }
  }, [baseBE_URL]);

  // Admin validation function - checks if current isAdmin value is correct
  const validateAdminStatus = async () => {
    if (!authToken || !isAdmin) return;

    try {
      // Make a simple API call to verify admin status
      // This should be a lightweight endpoint that returns user info including admin status
      const response = await fetch(
        `${window.location.protocol}//${
          window.location.hostname === "app.speqqle.com"
            ? "beprod1.speqqle.com"
            : window.location.hostname === "lab.speqqle.com"
            ? "belab.speqqle.com"
            : process.env.REACT_APP_USE_LOCALHOST_BE === "true" || process.env.REACT_APP_USE_LOCALHOST_BE === "True"
            ? "localhost:8000"
            : "belab.speqqle.com"
        }/api/v1/users/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const userData = await response.json();
        const actualAdminStatus = hasProperty(userData, "is_superuser") ? userData.is_superuser : false;
       
        // If the stored isAdmin doesn't match the actual status, correct it
        if (isAdmin !== actualAdminStatus) {
          console.warn(
            `Admin status mismatch detected! Stored: ${isAdmin}, Actual: ${actualAdminStatus}. Correcting...`
          );
          setIsAdmin(actualAdminStatus);
          // Also update sessionStorage
          safeSessionStorageSet("isAdmin", JSON.stringify(actualAdminStatus));
        }
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid, reset auth
        console.warn(
          "Invalid token detected during admin validation. Resetting auth."
        );
        resetAuth();
      }
    } catch (error) {
      console.error("Error validating admin status:", error);
      // Don't change isAdmin on network errors to avoid false negatives
    }
  };

  // Periodic admin validation - only runs when user is admin
  useEffect(() => {
    if (!isAuthReady || !authToken || !isAdmin) return;

    // Validate immediately
    validateAdminStatus();

    // Set up periodic validation every 30 seconds
    const interval = setInterval(validateAdminStatus, 30000);

    return () => clearInterval(interval);
  }, [isAuthReady, authToken, isAdmin]);

  // Additional protection: Monitor sessionStorage changes - only when user becomes admin
  useEffect(() => {
    if (!isAuthReady) return;

    const handleStorageChange = (e) => {
      if (e.key === "isAdmin" && e.newValue !== null) {
        const newAdminValue = JSON.parse(e.newValue);
        // Only validate if someone is trying to set isAdmin to true
        if (newAdminValue === true && isAdmin !== true) {
          console.warn(
            `SessionStorage isAdmin changed externally! Detected: ${newAdminValue}, Current: ${isAdmin}. Validating...`
          );
          // Validate the change with the server
          validateAdminStatus();
        }
      }
    };

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also monitor direct sessionStorage access (same tab)
    const originalSetItem = window.sessionStorage.setItem;
    window.sessionStorage.setItem = function (key, value) {
      if (key === "isAdmin") {
        const newAdminValue = JSON.parse(value);
        // Only validate if someone is trying to set isAdmin to true
        if (newAdminValue === true && isAdmin !== true) {
          console.warn(
            `SessionStorage isAdmin changed directly! Detected: ${newAdminValue}, Current: ${isAdmin}. Validating...`
          );
          // Validate the change with the server
          setTimeout(() => validateAdminStatus(), 100);
        }
      }
      return originalSetItem.apply(this, arguments);
    };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.sessionStorage.setItem = originalSetItem;
    };
  }, [isAuthReady, isAdmin, validateAdminStatus]);

  // Helper function to check if current access level meets or exceeds required level
  const checkAccessLevel = (requiredLevel) => {
    // If access level is -1 (not authenticated), return false
    if (accessLevel === -1) {
      return false;
    }
    // Check if current access level is equal to or higher than required level
    return accessLevel >= requiredLevel;
  };

  const value = {
    resetAuth,
    authToken,
    setAuthToken,
    isAdmin,
    setIsAdmin,
    isActive,
    setIsActive,
    accessLevel,
    setAccessLevel,
    checkAccessLevel,
    isAuthReady,
    setIsAuthReady,
    packages,
    setPackages,
    currentUser,
    setCurrentUser,
    isFirstLogin,
    setIsFirstLogin,
    baseBE_URL,
    setBaseBE_URL,
    syncWithSessionStorage,
    validateAdminStatus,
  };

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

export default AuthContextProvider;
